/*
 * Eigen winkelwagen (cart drawer) voor Anna & Jane Melbourne.
 *
 * Versie-onafhankelijk: leunt alleen op het openbare Shopify Ajax-cart-API
 * (/cart.js, /cart/add.js, /cart/change.js) en op een paar instelbare
 * selectors uit de thema-instellingen. Geen Impulse-interne modules, geen
 * externe host, geen tracking.
 *
 * Twee delen:
 *   1. EigenCartLogic: zuivere functies (geldige cart, korting per regel,
 *      geld), los te toetsen zonder browser.
 *   2. De drawer zelf (alleen als #EigenCart op de pagina staat).
 *
 * Regel voor de korting: de winkel past de kortingen zelf toe. De cart toont
 * alleen het RESULTAAT: het label op de regel (de echte titel uit cart.js),
 * in de voet de Discounts-regel (titels en total_discount uit cart.js, alleen
 * als die groter is dan nul) en het totaal. De regels tonen het bedrag voor de
 * korting, zodat regels min Discounts optellen tot het totaal. Geen eigen
 * kortingstekst, geen eigen percentage, geen staffel. Onbekend of onzinnig is
 * nooit nul: dan de foutstand.
 */
(function (root) {
  'use strict';

  // ---------------------------------------------------------------------
  // 1. Zuivere functies
  // ---------------------------------------------------------------------

  function isInt(n) {
    return typeof n === 'number' && isFinite(n) && Math.floor(n) === n;
  }

  /** Is dit een bruikbare cart.js- of cart/change.js-respons? */
  function cartIsGeldig(cart) {
    if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items)) return false;
    if (!isInt(cart.total_price) || cart.total_price < 0) return false;
    if (!isInt(cart.item_count) || cart.item_count < 0) return false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it || !isInt(it.quantity) || it.quantity < 1) return false;
      if (!isInt(it.final_line_price)) return false;
    }
    return true;
  }

  /** Kortingstitels per regel (voor het label naast de regel, zoals "AUTO 5" bij Upcart). */
  function regelLabels(item) {
    var titels = [];
    function voeg(t) {
      if (typeof t === 'string' && t.trim() && titels.indexOf(t.trim()) < 0) titels.push(t.trim());
    }
    var plat = item && Array.isArray(item.discounts) ? item.discounts : [];
    var regel = item && Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
    plat.forEach(function (d) { if (d && d.amount > 0) voeg(d.title); });
    regel.forEach(function (a) {
      if (a && a.amount > 0 && a.discount_application) voeg(a.discount_application.title);
    });
    return titels;
  }

  /**
   * De prijs die de regel toont: het bedrag VOOR de korting van de winkel (original_line_price). De korting staat
   * apart in de Discounts-regel; zo tellen de regels min Discounts op tot het totaal. Ontbreekt original_line_price,
   * dan final_line_price (die is dan het enige bedrag dat Shopify gaf).
   */
  function regelPrijs(item) {
    if (item && isInt(item.original_line_price)) return item.original_line_price;
    return item && isInt(item.final_line_price) ? item.final_line_price : null;
  }

  /**
   * De echte titels van de toegepaste kortingen in de hele cart, uit alle drie de bronnen van cart.js:
   * cart_level_discount_applications, items[].discounts en items[].line_level_discount_allocations[].discount_application.
   * Elke unieke titel een keer, op volgorde van het bedrag (groot naar klein, bij gelijk bedrag de volgorde van
   * verschijnen). Een korting zonder titel geeft geen label.
   */
  function kortingTitels(cart) {
    var namen = [];
    var bedragen = [];
    function zet(titel, bedrag, bron) {
      if (typeof titel !== 'string' || !titel.trim()) return;
      var t = titel.trim();
      var i = namen.indexOf(t);
      if (i < 0) { namen.push(t); bedragen.push([0, 0, 0]); i = namen.length - 1; }
      bedragen[i][bron] += bedrag;
    }
    var apps = cart && Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [];
    apps.forEach(function (a) {
      if (!a) return;
      var b = a.total_allocated_amount;
      // Een toegepaste korting zonder bedrag telt als bekend maar onbepaald (1); een bedrag van nul telt niet.
      if (b === undefined || b === null) b = 1;
      if (typeof b === 'number' && b > 0) zet(a.title, b, 0);
    });
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      var plat = item && Array.isArray(item.discounts) ? item.discounts : [];
      var regel = item && Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
      plat.forEach(function (d) { if (d && typeof d.amount === 'number' && d.amount > 0) zet(d.title, d.amount, 1); });
      regel.forEach(function (a) {
        if (a && typeof a.amount === 'number' && a.amount > 0 && a.discount_application) zet(a.discount_application.title, a.amount, 2);
      });
    });
    var rij = namen.map(function (naam, i) {
      return { naam: naam, volgorde: i, bedrag: Math.max(bedragen[i][0], bedragen[i][1], bedragen[i][2]) };
    });
    rij.sort(function (a, b) { return (b.bedrag - a.bedrag) || (a.volgorde - b.volgorde); });
    return rij.map(function (r) { return r.naam; });
  }

  /**
   * De Discounts-regel: alleen als Shopify een korting rekent (total_discount > 0). Het bedrag is total_discount van
   * Shopify, de titels zijn die van de winkel; er is geen eigen percentage en geen eigen berekening.
   */
  function kortingRegel(cart) {
    if (!cart || !isInt(cart.total_discount) || cart.total_discount <= 0) return null;
    return { bedrag: cart.total_discount, titels: kortingTitels(cart) };
  }

  /** De zin voor een schermlezer: "Discounts, 15% EXTRA OFF, minus $35.98". */
  function kortingZin(woord, titels, minus, bedragTekst) {
    return [woord].concat(titels, [minus + ' ' + bedragTekst]).join(', ');
  }

  /**
   * Vergelijkingsprijs (site2, 28-10; de rij "besparing" is op 29-10 verwijderd): de vergelijkingsprijs (compare_at_price van de variant, uit /products/<handle>.js) is de
   * enige bron van een doorgestreepte prijs; cart.js heeft er geen. Een vergelijkingsprijs telt alleen als hij een
   * geheel getal is, hoger dan de prijs die de klant betaalt per stuk (original_price) en op ,95 eindigt (de prijsregel van
   * de winkel). Anders geen doorstreping en geen claim: nooit een verzonnen of afgeronde "van"-prijs.
   */
  function vergelijkRegel(item, vergelijkPerStuk) {
    if (!item || !isInt(vergelijkPerStuk) || !isInt(item.original_price) || !isInt(item.quantity)) return null;
    if (vergelijkPerStuk <= item.original_price || vergelijkPerStuk % 100 !== 95) return null;
    return vergelijkPerStuk * item.quantity;
  }

  /**
   * Een productlink uit cart.js is alleen bruikbaar als het een pad op deze
   * winkel is: begint met "/", niet met "//" of "/\" (andere host), zonder
   * stuurtekens (een tab of nieuwe regel in "/<tab>/host" wordt door de
   * browser weggehaald en geeft dan "//host"). Anders een lege tekst: geen link.
   */
  function veiligPad(url) {
    if (typeof url !== 'string') return '';
    if (url.charAt(0) !== '/' || url.charAt(1) === '/' || url.charAt(1) === '\\') return '';
    if (/[\u0000-\u001f\u007f]/.test(url)) return '';
    return url;
  }

  // Geld ------------------------------------------------------------------

  function metScheiding(centen, decimalen, duizend, komma) {
    var negatief = centen < 0;
    var bedrag = (Math.abs(centen) / 100).toFixed(decimalen);
    var delen = bedrag.split('.');
    var heel = delen[0].replace(/\B(?=(\d{3})+(?!\d))/g, duizend);
    return (negatief ? '-' : '') + heel + (delen[1] ? komma + delen[1] : '');
  }

  /**
   * Formatteert centen met het money_format van de winkel
   * ("${{amount}}" e.d.). HTML in het formaat wordt weggehaald.
   */
  function geld(centen, formaat) {
    if (!isInt(centen)) return '';
    var f = (typeof formaat === 'string' && formaat) ? formaat.replace(/<[^>]*>/g, '') : '${{amount}}';
    var m = /\{\{\s*(\w+)\s*\}\}/.exec(f);
    var soort = m ? m[1] : 'amount';
    var w;
    switch (soort) {
      case 'amount_no_decimals': w = metScheiding(centen, 0, ',', '.'); break;
      case 'amount_with_comma_separator': w = metScheiding(centen, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': w = metScheiding(centen, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': w = metScheiding(centen, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': w = metScheiding(centen, 0, ' ', ','); break;
      case 'amount_with_space_separator': w = metScheiding(centen, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': w = metScheiding(centen, 2, ' ', '.'); break;
      default: w = metScheiding(centen, 2, ',', '.');
    }
    return m ? f.replace(m[0], w) : w;
  }

  /**
   * Geld in de valuta van de WINKELWAGEN. Is dat de valuta van de winkel,
   * dan het money_format van de winkel (gelijk aan de rest van het thema);
   * anders Intl met de echte valutacode, zodat er nooit een verkeerd
   * valutateken voor een bedrag staat.
   */
  function geldVoorCart(centen, cart, cfg) {
    var valuta = cart && cart.currency;
    if (!valuta || valuta === cfg.shopCurrency) return geld(centen, cfg.moneyFormat);
    try {
      return new Intl.NumberFormat(cfg.locale || 'en', { style: 'currency', currency: valuta }).format(centen / 100);
    } catch (e) {
      return valuta + ' ' + metScheiding(centen, 2, ',', '.');
    }
  }

  /**
   * MEL-STAAL3: het label dat de klant bij een productoptie ziet. Kleur toont "Color", maat toont "Size"; elke andere naam blijft zoals hij is.
   * Alleen weergave: de optienaam in /cart.js en in de productdata verandert niet (zelfde tabel als snippets/ajm-optielabel.liquid).
   */
  var OPTIE_LABELS = {
    color: 'Color', colour: 'Color', colours: 'Color', colors: 'Color', couleur: 'Color', couleurs: 'Color', kleur: 'Color', kleuren: 'Color',
    farbe: 'Color', farben: 'Color', colore: 'Color', colori: 'Color', 'f\u00e4rg': 'Color', farg: 'Color', cor: 'Color', cores: 'Color',
    size: 'Size', sizes: 'Size', maat: 'Size', maten: 'Size', taille: 'Size', tailles: 'Size', 'gr\u00f6\u00dfe': 'Size', 'gr\u00f6sse': 'Size',
    groesse: 'Size', grosse: 'Size', storlek: 'Size', talla: 'Size', tallas: 'Size', taglia: 'Size', taglie: 'Size', tamanho: 'Size', 'tama\u00f1o': 'Size'
  };
  function optieLabel(naam) {
    var s = String(naam == null ? '' : naam).replace(/^\s+|\s+$/g, '');
    var d = s.toLowerCase();
    return Object.prototype.hasOwnProperty.call(OPTIE_LABELS, d) ? OPTIE_LABELS[d] : s;
  }

  var logic = {
    cartIsGeldig: cartIsGeldig,
    regelLabels: regelLabels,
    regelPrijs: regelPrijs,
    kortingTitels: kortingTitels,
    kortingRegel: kortingRegel,
    kortingZin: kortingZin,
    vergelijkRegel: vergelijkRegel,
    veiligPad: veiligPad,
    geld: geld,
    geldVoorCart: geldVoorCart,
    optieLabel: optieLabel
  };
  root.EigenCartLogic = logic;
  if (typeof module !== 'undefined' && module.exports) module.exports = logic;

  // ---------------------------------------------------------------------
  // 2. De drawer
  // ---------------------------------------------------------------------

  if (typeof document === 'undefined') return;

  function start() {
    var el = document.getElementById('EigenCart');
    if (!el || el.__eigenCart) return;
    el.__eigenCart = true;

    var cfg = {};
    try {
      cfg = JSON.parse(document.getElementById('EigenCartConfig').textContent);
    } catch (e) {
      cfg = {};
    }
    var routes = cfg.routes || {};
    var r = {
      cart: routes.cart || '/cart.js',
      add: routes.add || '/cart/add.js',
      change: routes.change || '/cart/change.js',
      cartPage: routes.cartPage || '/cart'
    };
    var t = cfg.teksten || {};

    var paneel = el.querySelector('[data-ec-paneel]');
    var lijstEl = el.querySelector('[data-ec-regels]');
    var leegEl = el.querySelector('[data-ec-leeg]');
    var foutEl = el.querySelector('[data-ec-fout]');
    var voetEl = el.querySelector('[data-ec-voet]');
    var totEl = el.querySelector('[data-ec-totaal]');
    var korEl = el.querySelector('[data-ec-korting]');
    var korZinEl = el.querySelector('[data-ec-korting-zin]');
    var korLabelsEl = el.querySelector('[data-ec-korting-labels]');
    var korBedragEl = el.querySelector('[data-ec-korting-bedrag]');
    var meldEl = el.querySelector('[data-ec-melding]');
    var ladenEl = el.querySelector('[data-ec-laden]');
    var laatsteFocus = null;
    var wachtrij = Promise.resolve();
    var cart = null;

    // Elk verzoek eindigt na timeoutMs (standaard 15 s): antwoordt de winkel niet, dan krijgt de bezoeker de foutstand
    // of een melding in plaats van een lade die eeuwig op laden of op de oude stand blijft staan.
    var timeoutMs = isInt(cfg.timeoutMs) && cfg.timeoutMs > 0 ? cfg.timeoutMs : 15000;

    function json(url, opties) {
      opties = opties || {};
      opties.headers = Object.assign({ Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, opties.headers || {});
      opties.credentials = 'same-origin';
      var afbreker = typeof AbortController === 'function' ? new AbortController() : null;
      var verlopen = false;
      var timer = null;
      if (afbreker) {
        opties.signal = afbreker.signal;
        timer = setTimeout(function () { verlopen = true; afbreker.abort(); }, timeoutMs);
      }
      return fetch(url, opties).then(function (res) {
        return res.text().then(function (txt) {
          var data = null;
          try { data = txt ? JSON.parse(txt) : null; } catch (e) { data = null; }
          if (!res.ok) {
            var fout = new Error((data && (data.description || data.message)) || ('HTTP ' + res.status));
            fout.status = res.status;
            throw fout;
          }
          return data;
        });
      }).then(function (data) {
        clearTimeout(timer);
        return data;
      }, function (fout) {
        clearTimeout(timer);
        if (verlopen) {
          var te = new Error(t.fout || 'Something went wrong.');
          te.timeout = true;
          throw te;
        }
        throw fout;
      });
    }

    function melding(tekst) {
      meldEl.textContent = tekst || '';
      meldEl.hidden = !tekst;
    }

    function zetBubbel(aantal) {
      if (!cfg.countSelector) return;
      var lijst;
      try { lijst = document.querySelectorAll(cfg.countSelector); } catch (e) { return; }
      Array.prototype.forEach.call(lijst, function (b) {
        if (cfg.countVisibleClass) b.classList.toggle(cfg.countVisibleClass, aantal > 0);
        b.setAttribute('data-eigen-cart-count', String(aantal));
      });
    }

    function knop(klasse, label, inhoud) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = klasse;
      b.setAttribute('aria-label', label);
      if (inhoud) b.appendChild(inhoud);
      return b;
    }

    function svg(pad, viewBox) {
      var ns = 'http://www.w3.org/2000/svg';
      var s = document.createElementNS(ns, 'svg');
      s.setAttribute('viewBox', viewBox || '0 0 24 24');
      s.setAttribute('aria-hidden', 'true');
      s.setAttribute('focusable', 'false');
      var p = document.createElementNS(ns, 'path');
      p.setAttribute('d', pad);
      s.appendChild(p);
      return s;
    }

    function beeldUrl(url) {
      if (!url) return '';
      try {
        var u = new URL(url, location.href);
        u.searchParams.set('width', '200');
        return u.toString();
      } catch (e) {
        return url;
      }
    }

    function regel(item, index) {
      var li = document.createElement('li');
      li.className = 'ec-regel';
      li.setAttribute('data-ec-key', item.key);

      var fotoLink = document.createElement('a');
      fotoLink.className = 'ec-regel__foto';
      var pad = veiligPad(item.url);
      if (pad) fotoLink.href = pad;
      fotoLink.tabIndex = -1;
      fotoLink.setAttribute('aria-hidden', 'true');
      if (item.image) {
        var img = document.createElement('img');
        img.src = beeldUrl(item.image);
        img.alt = '';
        img.loading = 'lazy';
        img.width = 100;
        img.height = 100;
        fotoLink.appendChild(img);
      }
      li.appendChild(fotoLink);

      var info = document.createElement('div');
      info.className = 'ec-regel__info';

      var kop = document.createElement('div');
      kop.className = 'ec-regel__kop';
      var naam = document.createElement('a');
      naam.className = 'ec-regel__naam';
      if (pad) naam.href = pad;
      naam.textContent = item.product_title || item.title || '';
      kop.appendChild(naam);
      var weg = knop('ec-regel__weg', (t.remove || 'Remove') + ': ' + (item.product_title || ''),
        svg('M5.5 6.5h13M9.5 6.5v-2h5v2M7 6.5l.8 12a1.5 1.5 0 0 0 1.5 1.4h5.4a1.5 1.5 0 0 0 1.5-1.4l.8-12M10.5 10v6M13.5 10v6'));
      weg.setAttribute('data-ec-actie', 'weg');
      kop.appendChild(weg);
      info.appendChild(kop);

      var opties = (item.options_with_values || []).filter(function (o) {
        return o && o.value && o.value !== 'Default Title';
      });
      opties.forEach(function (o) {
        var p = document.createElement('div');
        p.className = 'ec-regel__optie';
        p.textContent = optieLabel(o.name) + ': ' + o.value;
        info.appendChild(p);
      });
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        var sp = document.createElement('div');
        sp.className = 'ec-regel__optie';
        sp.textContent = item.selling_plan_allocation.selling_plan.name;
        info.appendChild(sp);
      }
      Object.keys(item.properties || {}).forEach(function (k) {
        var v = item.properties[k];
        if (!v || k.charAt(0) === '_') return;
        var pr = document.createElement('div');
        pr.className = 'ec-regel__optie';
        pr.textContent = k + ': ' + v;
        info.appendChild(pr);
      });

      // Het bedrag VOOR de korting van de winkel: de korting staat in de Discounts-regel van de voet, zodat
      // de regels min Discounts optellen tot het totaal.
      var prijs = document.createElement('div');
      prijs.className = 'ec-regel__prijs';
      var nu = document.createElement('span');
      nu.textContent = geldVoorCart(regelPrijs(item), cart, cfg);
      prijs.appendChild(nu);
      info.appendChild(prijs);

      var onder = document.createElement('div');
      onder.className = 'ec-regel__onder';

      var aantal = document.createElement('div');
      aantal.className = 'ec-aantal';
      aantal.setAttribute('role', 'group');
      aantal.setAttribute('aria-label', (t.quantity || 'Quantity') + ': ' + (item.product_title || ''));
      var min = knop('ec-aantal__knop', t.reduce || 'Reduce item quantity by one', svg('M5 12h14'));
      min.setAttribute('data-ec-actie', 'min');
      var veld = document.createElement('input');
      veld.className = 'ec-aantal__veld';
      veld.type = 'number';
      veld.inputMode = 'numeric';
      veld.min = '0';
      veld.value = String(item.quantity);
      veld.id = 'EigenCartAantal-' + index;
      veld.setAttribute('aria-label', t.quantity || 'Quantity');
      veld.setAttribute('data-ec-actie', 'veld');
      var plus = knop('ec-aantal__knop', t.increase || 'Increase item quantity by one', svg('M12 5v14M5 12h14'));
      plus.setAttribute('data-ec-actie', 'plus');
      aantal.appendChild(min);
      // Het veld staat in een label dat naar het veld wijst: een tik op de onzichtbare strips boven en onder het veld
      // (CSS, .ec-aantal__vak) focust het veld, zodat het tapdoel 44 px hoog is zonder dat het veld er anders uitziet.
      var vak = document.createElement('label');
      vak.className = 'ec-aantal__vak';
      vak.htmlFor = veld.id;
      vak.appendChild(veld);
      aantal.appendChild(vak);
      aantal.appendChild(plus);
      onder.appendChild(aantal);

      // MEL-SAMEN1 (F5): geen label per regel. Dezelfde korting stond bij het product EN als rij "Discounts" in de voet (dubbel);
      // nu staat elke korting een keer, in de Discounts-rij (naam en bedrag). regelLabels() blijft bestaan voor de toetsen.
      info.appendChild(onder);

      var fout = document.createElement('div');
      fout.className = 'ec-regel__fout';
      fout.setAttribute('role', 'alert');
      fout.hidden = true;
      info.appendChild(fout);

      li.appendChild(info);
      return li;
    }

    function wisKorting() {
      if (!korEl) return;
      korEl.hidden = true;
      korZinEl.textContent = '';
      korLabelsEl.textContent = '';
      korBedragEl.textContent = '';
    }

    // De Discounts-regel in de voet: alleen als Shopify een korting rekent. Titels en bedrag komen uit dezelfde
    // /cart.js als de rest, alles met textContent.
    function zetKorting() {
      wisKorting();
      if (!korEl) return;
      var k = kortingRegel(cart);
      if (!k) return;
      var bedragTekst = geldVoorCart(k.bedrag, cart, cfg);
      if (!bedragTekst) return;
      var minusTekst = '- ' + bedragTekst;
      k.titels.forEach(function (titel) {
        var lab = document.createElement('span');
        lab.className = 'ec-korting__label';
        lab.appendChild(svg('M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9-9-9Zm5-5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z'));
        var tx = document.createElement('span');
        tx.textContent = titel;
        lab.appendChild(tx);
        korLabelsEl.appendChild(lab);
      });
      korBedragEl.textContent = minusTekst;
      korZinEl.textContent = kortingZin(t.discounts || 'Discounts', k.titels, t.minus || 'minus', bedragTekst);
      korEl.hidden = false;
    }

    // Vergelijkingsprijzen (site2): per product EEN keer /products/<handle>.js (zelfde herkomst, geen derde partij); een mislukt
    // verzoek geeft null en dus geen doorstreping. Een cart in een andere valuta dan de winkel wordt niet
    // verrijkt (de bedragen van product.js en cart.js zouden dan niet bewezen in dezelfde valuta staan).
    var vergelijkCache = {};

    function haalVergelijk(handle) {
      if (!vergelijkCache[handle]) {
        vergelijkCache[handle] = json('/products/' + encodeURIComponent(handle) + '.js').then(function (p) {
          var m = {};
          ((p && p.variants) || []).forEach(function (v) {
            if (v && isInt(v.id) && isInt(v.compare_at_price)) m[v.id] = v.compare_at_price;
          });
          return m;
        }, function () { return null; });
      }
      return vergelijkCache[handle];
    }

    function verrijk(deze) {
      if (cfg.toonBesparing === false || !deze || !deze.items || deze.currency !== cfg.shopCurrency) return;
      var handles = [];
      deze.items.forEach(function (i) { if (i.handle && handles.indexOf(i.handle) < 0) handles.push(i.handle); });
      Promise.all(handles.map(haalVergelijk)).then(function (lijst) {
        if (cart !== deze) return; // intussen is er een nieuwere stand
        var verg = {};
        handles.forEach(function (h, k) { if (lijst[k]) Object.keys(lijst[k]).forEach(function (id) { verg[id] = lijst[k][id]; }); });
        deze.items.forEach(function (item) {
          var v = vergelijkRegel(item, verg[item.variant_id]);
          if (v === null) return;
          var prijsEl = lijstEl.querySelector(regelSelector(item.key) + ' .ec-regel__prijs');
          if (!prijsEl || prijsEl.querySelector('s')) return;
          var sr = document.createElement('span');
          sr.className = 'ec-sr';
          sr.textContent = ' ' + (t.oorspronkelijk || 'Original price') + ' ';
          var oud = document.createElement('s');
          oud.textContent = geldVoorCart(v, deze, cfg);
          prijsEl.appendChild(sr);
          prijsEl.appendChild(oud);
        });
      });
    }

    function toonFout() {
      // Foutstand: geen bedragen, geen nul, wel een uitweg naar de cartpagina.
      cart = null;
      if (ladenEl) ladenEl.hidden = true;
      lijstEl.textContent = '';
      leegEl.hidden = true;
      voetEl.hidden = true;
      wisKorting();
      foutEl.hidden = false;
      el.setAttribute('data-ec-stand', 'fout');
    }

    function render(data) {
      if (!cartIsGeldig(data)) { toonFout(); return; }
      cart = data;
      if (ladenEl) ladenEl.hidden = true;
      foutEl.hidden = true;
      lijstEl.textContent = '';
      zetBubbel(cart.item_count);

      if (!cart.items.length) {
        leegEl.hidden = false;
        voetEl.hidden = true;
        wisKorting();
        el.setAttribute('data-ec-stand', 'leeg');
        return;
      }
      leegEl.hidden = true;
      voetEl.hidden = false;
      el.setAttribute('data-ec-stand', 'gevuld');
      cart.items.forEach(function (item, i) { lijstEl.appendChild(regel(item, i)); });

      zetKorting();
      totEl.textContent = geldVoorCart(cart.total_price, cart, cfg);
      verrijk(cart);
    }

    // Stand "laden": geen oude regels of bedragen meer, tot /cart.js antwoordt (of de timeout de foutstand geeft).
    function zetLaden() {
      cart = null;
      lijstEl.textContent = '';
      leegEl.hidden = true;
      foutEl.hidden = true;
      voetEl.hidden = true;
      wisKorting();
      melding('');
      if (ladenEl) ladenEl.hidden = false;
      el.setAttribute('data-ec-stand', 'laden');
    }

    function laad() {
      return json(r.cart).then(render, function () { toonFout(); });
    }

    function inRij(taak) {
      wachtrij = wachtrij.then(taak, taak);
      return wachtrij;
    }

    // Selector van een regel op sleutel: overal dezelfde, met CSS.escape (een sleutel kan " \ : of ] bevatten).
    function regelSelector(key) {
      return '[data-ec-key="' + (window.CSS && CSS.escape ? CSS.escape(key) : String(key).replace(/["\\]/g, '\\$&')) + '"]';
    }

    // aantal: een getal, of een functie die het nieuwe aantal bepaalt. Die functie draait pas als de taak aan de beurt is,
    // dus op de cart zoals die na de vorige wijziging is gelezen: twee plussen vlak na elkaar met het toetsenbord geven +2.
    // Geeft de functie null (de regel bestaat niet meer, of de cart is niet te lezen), dan gebeurt er niets.
    function wijzig(key, bepaalAantal, li) {
      return inRij(function () {
        var aantal = typeof bepaalAantal === 'function' ? bepaalAantal() : bepaalAantal;
        if (aantal === null) return;
        el.setAttribute('aria-busy', 'true');
        melding('');
        return json(r.change, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: aantal })
        }).then(function () {
          // Altijd opnieuw /cart.js lezen: één vorm, en de kortingen zoals ze nu gelden.
          return laad();
        }).catch(function (fout) {
          return laad().then(function () {
            var nieuw = lijstEl.querySelector(regelSelector(key) + ' .ec-regel__fout');
            var tekst = fout && fout.message ? fout.message : (t.fout || 'Something went wrong.');
            if (nieuw) { nieuw.textContent = tekst; nieuw.hidden = false; } else { melding(tekst); }
          });
        }).then(function () {
          el.removeAttribute('aria-busy');
        });
      });
    }

    // Openen, sluiten, focus --------------------------------------------

    function focusbaar() {
      return Array.prototype.filter.call(
        paneel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (n) { return n.tabIndex >= 0 && !n.closest('[hidden]') && n.offsetParent !== null; }
      );
    }

    // naarLaden: de aanroeper leest daarna /cart.js zelf; tot dat antwoord staat de lade op laden.
    function open(bron, naarLaden) {
      if (el.classList.contains('is-open')) return;
      if (naarLaden) zetLaden();
      // Opent de lade terwijl hij nog sluit: de lopende afsluiting vervalt, de overgang keert om.
      stopSluiten();
      laatsteFocus = bron || document.activeElement;
      el.hidden = false;
      // reflow, zodat de overgang loopt
      void el.offsetWidth;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ec-zonder-scroll');
      var sluit = el.querySelector('[data-ec-sluit-knop]');
      if (sluit) sluit.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('eigen-cart:open'));
    }

    // Sluiten loopt als openen, achterstevoren: eerst gaat is-open eraf en spelen de overgangen van .ec-paneel en
    // .ec-achtergrond uit, pas daarna gaat de lade op hidden (display:none kapt elke overgang af).
    // Zonder bewegingsvoorkeur (prefers-reduced-motion) of zonder overgang blijft het direct.
    var sluitTimer = null;
    var sluitKlaar = null;

    function stopSluiten() {
      if (sluitTimer) { clearTimeout(sluitTimer); sluitTimer = null; }
      if (sluitKlaar) { paneel.removeEventListener('transitionend', sluitKlaar); sluitKlaar = null; }
    }

    function eindeSluiten() {
      stopSluiten();
      if (el.classList.contains('is-open')) return;
      el.hidden = true;
      document.documentElement.classList.remove('ec-zonder-scroll');
    }

    function sluit() {
      if (!el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      if (laatsteFocus && typeof laatsteFocus.focus === 'function' && document.contains(laatsteFocus)) laatsteFocus.focus({ preventScroll: true });
      var beweegt = !(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches)
        && getComputedStyle(paneel).transitionDuration !== '0s';
      stopSluiten();
      if (beweegt) {
        sluitKlaar = function (e) { if (e.target === paneel && e.propertyName === 'transform') eindeSluiten(); };
        paneel.addEventListener('transitionend', sluitKlaar);
        sluitTimer = setTimeout(eindeSluiten, 350); // vangnet als transitionend uitblijft (tabblad op de achtergrond)
      } else {
        eindeSluiten();
      }
      document.dispatchEvent(new CustomEvent('eigen-cart:close'));
    }

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-ec-sluit]')) { e.preventDefault(); sluit(); return; }
      var actieEl = e.target.closest('[data-ec-actie]');
      if (!actieEl || actieEl.getAttribute('data-ec-actie') === 'veld') return;
      var li = actieEl.closest('[data-ec-key]');
      if (!li || !cart) return;
      var key = li.getAttribute('data-ec-key');
      var item = cart.items.filter(function (i) { return i.key === key; })[0];
      if (!item) return;
      var actie = actieEl.getAttribute('data-ec-actie');
      var verschil = actie === 'plus' ? 1 : actie === 'min' ? -1 : null;
      wijzig(key, function () {
        var nu = cart && cart.items.filter(function (i) { return i.key === key; })[0];
        if (!nu) return null;
        return verschil === null ? 0 : Math.max(0, nu.quantity + verschil);
      }, li).then(function () {
        // focus terug op dezelfde knop van dezelfde regel, of op de sluitknop
        var zelfde = lijstEl.querySelector(regelSelector(key) + ' [data-ec-actie="' + actie + '"]');
        (zelfde || el.querySelector('[data-ec-sluit-knop]')).focus();
      });
    });

    el.addEventListener('change', function (e) {
      if (!e.target.matches('[data-ec-actie="veld"]')) return;
      var li = e.target.closest('[data-ec-key]');
      var n = parseInt(e.target.value, 10);
      if (!li || !isFinite(n) || n < 0) { laad(); return; }
      wijzig(li.getAttribute('data-ec-key'), n, li);
    });

    // Toetsenbord, in de capture-fase op document: zolang de drawer open is
    // horen Escape en Tab bij de dialoog en niet bij handlers van het thema
    // (Impulse sluit bij Escape zijn zoekvenster en verplaatst dan de focus).
    document.addEventListener('keydown', function (e) {
      if (!el.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); sluit(); return; }
      if (e.key !== 'Tab') return;
      var f = focusbaar();
      if (!f.length) return;
      var eerste = f[0];
      var laatste = f[f.length - 1];
      if (!paneel.contains(document.activeElement)) { e.preventDefault(); e.stopPropagation(); (e.shiftKey ? laatste : eerste).focus(); }
      else if (e.shiftKey && document.activeElement === eerste) { e.preventDefault(); e.stopPropagation(); laatste.focus(); }
      else if (!e.shiftKey && document.activeElement === laatste) { e.preventDefault(); e.stopPropagation(); eerste.focus(); }
    }, true);

    // Invoermodus voor de focusring van de sluitknop (zie eigen-cart.css): toetsenbord aan, aanraking of muis uit.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Shift' && e.key !== 'Control' && e.key !== 'Alt' && e.key !== 'Meta') el.setAttribute('data-ec-toets', '');
    }, true);
    document.addEventListener('pointerdown', function () { el.removeAttribute('data-ec-toets'); }, true);

    // Cart-icoon --------------------------------------------------------

    document.addEventListener('click', function (e) {
      if (!cfg.openSelector || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a;
      try { a = e.target.closest(cfg.openSelector); } catch (err) { return; }
      if (!a || el.contains(a)) return;
      e.preventDefault();
      open(a, true);
      laad();
    }, true);

    // Toevoegen ---------------------------------------------------------

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!cfg.formSelector || !form || e.defaultPrevented) return;
      try { if (!form.matches(cfg.formSelector)) return; } catch (err) { return; }
      if (form.hasAttribute('data-eigen-cart-overslaan')) return;
      var submitter = e.submitter;
      if (submitter && submitter.name === 'checkout') return;
      e.preventDefault();
      var knopEl = submitter || form.querySelector('[type="submit"]');
      if (knopEl) { knopEl.setAttribute('aria-busy', 'true'); knopEl.disabled = true; }
      var fd = new FormData(form);
      inRij(function () {
        return json(r.add, { method: 'POST', body: fd }).then(function () {
          return laad();
        }).then(function () {
          melding('');
          open(knopEl);
        }, function (fout) {
          return laad().then(function () {
            melding(fout && fout.message ? fout.message : (t.fout || 'Something went wrong.'));
            open(knopEl);
          });
        }).then(function () {
          if (knopEl) { knopEl.removeAttribute('aria-busy'); knopEl.disabled = false; }
        });
      });
    }, true);

    // Een andere plek kan de drawer openen of verversen.
    document.addEventListener('eigen-cart:refresh', function () { laad(); });
    document.addEventListener('eigen-cart:open-request', function () { open(null, true); laad(); });

    root.EigenCart = { open: function () { open(null, true); return laad(); }, sluit: sluit, laad: laad };
    // Geen /cart.js-verzoek per paginabezoek: de drawer leest pas bij openen
    // (of na toevoegen). Tot dan staat hij in de stand "laden".
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : globalThis);
